import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  FileText, ExternalLink, CheckCircle, AlertTriangle, Shield,
  Settings, Key, ArrowRight, Building2, Zap, Database,
  Globe, Lock, Send, Copy, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const clearinghouses = [
  {
    id: 'availity',
    name: 'Availity',
    logo: 'A',
    color: '#0066CC',
    description: 'Largest multi-payer platform in the US. Connects to 2,000+ payers.',
    features: ['Real-time eligibility', 'Claim status inquiry', 'ERA/EOB processing', 'Prior authorization'],
    formats: ['EDI 837P/I', 'EDI 835', 'EDI 270/271', 'EDI 276/277'],
    portalUrl: 'https://www.availity.com',
    apiDocs: 'https://developer.availity.com',
    setupSteps: [
      'Register for an Availity account at availity.com',
      'Navigate to My Account > API Access to generate Client ID and Client Secret',
      'Configure your NPI and Tax ID in the Organization Settings',
      'Set the Availity API endpoint in Savenes Claims Settings',
      'Run a test claim submission to verify connectivity',
      'Enable production mode after successful testing'
    ]
  },
  {
    id: 'change',
    name: 'Change Healthcare (Optum)',
    logo: 'C',
    color: '#FF6600',
    description: 'Processes 15B+ transactions/year. Now part of UnitedHealth/Optum.',
    features: ['Claims submission', 'Remittance advice', 'Eligibility verification', 'Clinical data exchange'],
    formats: ['EDI 837P/I/D', 'EDI 835', 'EDI 270/271', 'X12 5010'],
    portalUrl: 'https://www.changehealthcare.com',
    apiDocs: 'https://developers.changehealthcare.com',
    setupSteps: [
      'Apply for a Change Healthcare developer account',
      'Obtain your Submitter ID and API credentials from the portal',
      'Configure your practice NPI and billing provider info',
      'Map Savenes claim fields to Change Healthcare X12 format',
      'Submit test claims through the sandbox environment',
      'Request production access after validation'
    ]
  },
  {
    id: 'waystar',
    name: 'Waystar',
    logo: 'W',
    color: '#00A86B',
    description: 'End-to-end revenue cycle technology. AI-powered denial prevention.',
    features: ['Predictive denial analytics', 'Automated claim scrubbing', 'Patient estimation', 'Appeals automation'],
    formats: ['EDI 837P/I', 'EDI 835', 'FHIR R4', 'HL7 v2'],
    portalUrl: 'https://www.waystar.com',
    apiDocs: 'https://developer.waystar.com',
    setupSteps: [
      'Contact Waystar sales for API access and credentials',
      'Install the Waystar Connector module in Savenes',
      'Enter your Organization ID, API Key, and Secret',
      'Map payer IDs between Savenes and Waystar',
      'Run the claim validation test suite',
      'Activate live claim routing after approval'
    ]
  },
  {
    id: 'trizetto',
    name: 'Trizetto (Cognizant)',
    logo: 'T',
    color: '#6B21A8',
    description: 'Healthcare IT solutions with connectivity to 8,000+ payers.',
    features: ['Batch claim submission', 'Real-time adjudication', 'Provider enrollment', 'Coordination of benefits'],
    formats: ['EDI 837P/I', 'EDI 835', 'EDI 270/271', 'NCPDP'],
    portalUrl: 'https://www.trizetto.com',
    apiDocs: 'https://developer.trizetto.com',
    setupSteps: [
      'Register as a Trizetto Gateway customer',
      'Request SFTP credentials or REST API access',
      'Configure your facility and provider details',
      'Set up payer routing tables for your region',
      'Submit test batch files for validation',
      'Transition to production after certification'
    ]
  }
];

export default function ClaimsIntegrationPage() {
  const [selectedCH, setSelectedCH] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ platform: '', username: '', password: '', api_key: '', api_secret: '' });
  const [credentials, setCredentials] = useState({});

  const handleSaveCredentials = () => {
    if (!loginForm.platform) return;
    setCredentials(prev => ({ ...prev, [loginForm.platform]: { ...loginForm, connected: true, connected_at: new Date().toISOString() } }));
    toast.success(`${clearinghouses.find(c => c.id === loginForm.platform)?.name} credentials saved`);
    setShowLogin(false);
    setLoginForm({ platform: '', username: '', password: '', api_key: '', api_secret: '' });
  };

  const openLogin = (ch) => {
    setLoginForm({ platform: ch.id, username: '', password: '', api_key: '', api_secret: '' });
    setShowLogin(true);
  };

  return (
    <div className="space-y-6" data-testid="claims-integration-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Claims Integration
          </h1>
          <p className="text-[#64748B] mt-1">Connect to leading claims clearinghouses for automated submission</p>
        </div>
      </div>

      {/* EDI Overview */}
      <Card className="careiq-card bg-gradient-to-r from-[#0A2342] to-[#0E7490] text-white">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <Database className="w-10 h-10 opacity-60 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold">How Claims Integration Works</h3>
              <p className="text-white/80 text-sm mt-1">
                Savenes generates HIPAA-compliant EDI 837 claim files from your patient encounters. 
                These are routed through a clearinghouse that validates, scrubs, and forwards claims to 
                the appropriate payer. Remittance (835) files flow back with payment details and denial reasons.
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">837P/I Claims</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                  <ArrowRight className="w-4 h-4" />
                  <span className="text-sm">Clearinghouse</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                  <ArrowRight className="w-4 h-4" />
                  <span className="text-sm">Payer</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-1.5">
                  <ArrowRight className="w-4 h-4" />
                  <span className="text-sm">835 Remittance</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clearinghouse Cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        {clearinghouses.map(ch => {
          const isConnected = credentials[ch.id]?.connected;
          return (
            <Card key={ch.id} className="careiq-card hover:shadow-md transition-shadow" data-testid={`ch-${ch.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: ch.color }}>
                      {ch.logo}
                    </div>
                    <div>
                      <div className="font-semibold text-[#0A2342]">{ch.name}</div>
                      <div className="text-xs text-[#64748B] mt-0.5">{ch.description}</div>
                    </div>
                  </div>
                  {isConnected && <CheckCircle className="w-5 h-5 text-[#166534] flex-shrink-0" />}
                </div>

                <div className="flex flex-wrap gap-1.5 mt-3">
                  {ch.features.map((f, i) => (
                    <span key={i} className="px-2 py-0.5 text-[10px] bg-[#F8FAFC] text-[#64748B] rounded border border-[#E2E8F0]">{f}</span>
                  ))}
                </div>

                <div className="flex flex-wrap gap-1.5 mt-2">
                  {ch.formats.map((f, i) => (
                    <span key={i} className="px-2 py-0.5 text-[10px] bg-[#0E7490]/10 text-[#0E7490] rounded font-mono">{f}</span>
                  ))}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white flex-1" onClick={() => openLogin(ch)} data-testid={`connect-${ch.id}`}>
                    {isConnected ? <CheckCircle className="w-3 h-3 mr-1" /> : <Key className="w-3 h-3 mr-1" />}
                    {isConnected ? 'Connected' : 'Connect'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setSelectedCH(ch); setShowSetup(true); }} data-testid={`setup-${ch.id}`}>
                    <Settings className="w-3 h-3 mr-1" /> Setup Guide
                  </Button>
                  <Button size="sm" variant="outline" asChild>
                    <a href={ch.portalUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Connection Status */}
      {Object.keys(credentials).length > 0 && (
        <Card className="careiq-card" data-testid="connection-status">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#166534]" /> Active Connections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(credentials).map(([id, cred]) => {
                const ch = clearinghouses.find(c => c.id === id);
                return (
                  <div key={id} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: ch?.color }}>
                        {ch?.logo}
                      </div>
                      <div>
                        <div className="font-medium text-sm text-[#0A2342]">{ch?.name}</div>
                        <div className="text-[10px] text-[#64748B]">Connected: {new Date(cred.connected_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <span className="px-2 py-1 text-xs bg-[#166534]/10 text-[#166534] rounded-full font-medium">Active</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Setup Guide Dialog */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="sm:max-w-lg" aria-describedby="setup-desc">
          <DialogHeader>
            <DialogTitle>{selectedCH?.name} Setup Guide</DialogTitle>
            <p id="setup-desc" className="text-sm text-[#64748B]">Follow these steps to integrate with {selectedCH?.name}</p>
          </DialogHeader>
          {selectedCH && (
            <div className="space-y-4 mt-2 max-h-96 overflow-auto">
              {selectedCH.setupSteps.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#0E7490] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{i + 1}</div>
                  <p className="text-sm text-[#334155] pt-0.5">{step}</p>
                </div>
              ))}
              <div className="p-3 bg-[#0E7490]/10 rounded-lg mt-4">
                <div className="text-sm font-medium text-[#0E7490] flex items-center gap-1"><Globe className="w-4 h-4" /> API Documentation</div>
                <a href={selectedCH.apiDocs} target="_blank" rel="noopener noreferrer" className="text-xs text-[#0E7490] underline mt-1 block">{selectedCH.apiDocs}</a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Connect/Login Dialog */}
      <Dialog open={showLogin} onOpenChange={setShowLogin}>
        <DialogContent className="sm:max-w-md" aria-describedby="login-desc">
          <DialogHeader>
            <DialogTitle>Connect to {clearinghouses.find(c => c.id === loginForm.platform)?.name}</DialogTitle>
            <p id="login-desc" className="text-sm text-[#64748B]">Enter your clearinghouse credentials to enable claim routing</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Username / Submitter ID</Label>
              <Input value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} placeholder="Your submitter ID" className="mt-1" data-testid="ch-username" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Account password" className="mt-1" data-testid="ch-password" />
            </div>
            <div>
              <Label>API Key (if applicable)</Label>
              <Input value={loginForm.api_key} onChange={(e) => setLoginForm({ ...loginForm, api_key: e.target.value })} placeholder="Optional API key" className="mt-1" data-testid="ch-api-key" />
            </div>
            <div>
              <Label>API Secret (if applicable)</Label>
              <Input type="password" value={loginForm.api_secret} onChange={(e) => setLoginForm({ ...loginForm, api_secret: e.target.value })} placeholder="Optional API secret" className="mt-1" data-testid="ch-api-secret" />
            </div>
            <div className="p-3 bg-[#92400E]/10 rounded-lg flex items-start gap-2">
              <Lock className="w-4 h-4 text-[#92400E] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#92400E]">Credentials are encrypted and stored securely in compliance with HIPAA regulations.</p>
            </div>
            <Button className="w-full bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" onClick={handleSaveCredentials} data-testid="save-ch-credentials">
              <Zap className="w-4 h-4 mr-2" /> Connect & Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
